<template>
  <div v-if="mounted" class="admin-page">
    <h1>Assignments CSV Upload</h1>

    <form @submit.prevent="submit">
      <div v-if="errors.general" class="form-error">{{ errors.general }}</div>

      <div class="form-field">
        <label for="csvFile">Choose Assignments CSV file</label>
        <input
            id="csvFile"
            type="file"
            accept=".csv"
            @change="onFileChange"
        />
      </div>

      <div v-if="fileName" class="form-info">
        Selected file: {{ fileName }} ({{ fileSize }} bytes)
      </div>

      <div v-if="errors.csvFile" class="form-error">{{ errors.csvFile }}</div>

      <div class="form-field">
        <button type="submit" :disabled="submitting">
          {{ submitting ? "Uploading..." : "Upload CSV" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { ValueService } from "@/services";

const service = new ValueService();

const mounted = ref(false);
const submitting = ref(false);

const file = ref<File | null>(null);
const fileName = ref("");
const fileSize = ref<number | null>(null);

const errors = ref<Record<string, string>>({});

onMounted(() => {
  mounted.value = true;
});

function onFileChange(event: Event) {
  errors.value.csvFile = "";
  file.value = null;
  fileName.value = "";
  fileSize.value = null;

  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    const selectedFile = target.files[0];

    // Basic sanity checks
    if (!selectedFile.name.endsWith(".csv")) {
      errors.value.csvFile = "File must be a .csv file";
      return;
    }
    if (selectedFile.size === 0) {
      errors.value.csvFile = "File is empty";
      return;
    }

    file.value = selectedFile;
    fileName.value = selectedFile.name;
    fileSize.value = selectedFile.size;
  }
}

async function submit() {
  errors.value.general = "";

  if (!file.value) {
    errors.value.csvFile = "Please choose a CSV file";
    return;
  }

  try {
    submitting.value = true;

    // Read file into text
    const text = await file.value.text();

    // Optional sanity check: at least one line with commas
    const lines = text.trim().split("\n");
    if (lines.length < 1 || !lines[0]!.includes(",")) {
      throw new Error("File does not look like valid CSV");
    }

    // Post to server
    await service.uploadAssignmentsCsv(fileName.value, text);

    // Reset
    file.value = null;
    fileName.value = "";
    fileSize.value = null;
    (document.getElementById("csvFile") as HTMLInputElement).value = "";

    alert("CSV uploaded successfully!");
  } catch (error: any) {
    console.error("CSV upload failed:", error);
    errors.value.general = error.message || "An error occurred during upload";
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped src="../admin.css"></style>